import type { GridColDef } from '@mui/x-data-grid-pro';
import type { IntelligenceRequest } from '@shared/domain/intelligenceRequest/IntelligenceRequest';
import { MoreVert } from '@mui/icons-material';
import { Box, Chip, IconButton, Typography } from '@mui/material';
import moment from 'moment';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import CompanyLogo from '@shared/components/CompanyLogo';
import { ModernDataTable } from '@shared/components/DataTable';

interface ConsultationRequestsTableProps {
  consultationRequests: IntelligenceRequest[];
  onMenuOpen: (event: React.MouseEvent<HTMLElement>, requestId: number) => void;
  title?: string;
  subtitle?: string;
}

export const ConsultationRequestsTable: React.FC<ConsultationRequestsTableProps> = ({
  consultationRequests,
  onMenuOpen,
  title = 'Consultation Requests',
  subtitle = 'Manage your incoming consultation requests (only visible to you)',
}) => {
  const navigate = useNavigate();

  const columns: GridColDef[] = [
    {
      field: 'createdAt',
      headerName: 'DATE REQUESTED',
      flex: 1,
      minWidth: 150,
      renderCell: params => moment(params.value).format('MM/DD/YYYY'),
    },
    {
      field: 'reviewId',
      headerName: 'REVIEW',
      flex: 1.5,
      minWidth: 180,
      renderCell: (params) => {
        const row = params.row as IntelligenceRequest;
        const reviewId = row.reviewId;
        const companyName = row.companyName || row.company?.name;
        const companyId = row.companyId || row.company?.id;

        if (!reviewId) {
          return (
            <Typography variant="body2" color="text.secondary">
              None
            </Typography>
          );
        }

        return (
          <Box
            sx={{
              'display': 'flex',
              'alignItems': 'center',
              'gap': 1,
              'cursor': 'pointer',
              '&:hover': {
                opacity: 0.8,
              },
            }}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/review-reader/${encodeURIComponent(companyName || 'company')}/${reviewId}`);
            }}
          >
            <CompanyLogo
              companyId={companyId}
              companyName={companyName || ''}
              alt={companyName || 'Company'}
              sx={{ width: 28, height: 28 }}
            />
            <Typography
              variant="body2"
              sx={{
                'fontWeight': 500,
                'color': '#0b74da',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              {companyName || 'View Review'}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'amountUsd',
      headerName: 'AMOUNT',
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        const amount = params.value as number | undefined;
        if (amount === undefined || amount === null)
          return '-';
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(amount);
      },
    },
    {
      field: 'engagementType',
      headerName: 'ENGAGEMENT TYPE',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        const type = params.value as string | undefined;
        if (!type)
          return '-';
        return type.charAt(0).toUpperCase() + type.slice(1);
      },
    },
    {
      field: 'status',
      headerName: 'STATUS',
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        const status = (params.value as string)?.toLowerCase() || '';

        const getStatusStyles = (status: string): { color?: string; bgcolor?: string } | undefined => {
          switch (status) {
            case 'active':
              return undefined; // Uses primary color from MUI
            case 'accepted_by_review_author':
              return { bgcolor: '#4caf50', color: '#fff' }; // Bright green
            case 'pending':
              return undefined; // Uses warning color from MUI
            case 'fulfilled':
              return { bgcolor: '#2e7d32', color: '#fff' }; // Dark green
            case 'cancelled':
            case 'rejected':
            case 'expired':
              return undefined; // Uses error color from MUI
            case 'paused':
              return undefined; // Uses default color from MUI
            default:
              return undefined;
          }
        };

        const getStatusColor = (status: string): 'success' | 'warning' | 'info' | 'error' | 'default' | 'primary' | undefined => {
          const customStyles = getStatusStyles(status);
          if (customStyles)
            return undefined; // Will use custom sx styles instead

          switch (status) {
            case 'active':
              return 'primary';
            case 'pending':
              return 'warning';
            case 'cancelled':
            case 'rejected':
            case 'expired':
              return 'error';
            case 'paused':
              return 'default';
            default:
              return 'default';
          }
        };

        const getStatusLabel = (status: string): string => {
          switch (status) {
            case 'active':
              return 'Active';
            case 'accepted_by_review_author':
              return 'Accepted';
            case 'pending':
              return 'Pending';
            case 'fulfilled':
              return 'Fulfilled';
            case 'cancelled':
              return 'Cancelled';
            case 'rejected':
              return 'Declined';
            case 'expired':
              return 'Expired';
            case 'paused':
              return 'Paused';
            default:
              return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
          }
        };

        const customStyles = getStatusStyles(status);

        return (
          <Chip
            label={getStatusLabel(status)}
            color={getStatusColor(status)}
            size="small"
            sx={{ fontWeight: 600, ...customStyles }}
          />
        );
      },
    },
    {
      field: 'actions',
      headerName: 'ACTIONS',
      width: 100,
      sortable: false,
      filterable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: params => (
        <IconButton
          onClick={e => onMenuOpen(e, params.row.id)}
          size="small"
          sx={{
            'color': 'text.secondary',
            'transition': 'all 0.2s ease',
            '&:hover': {
              color: '#0b74da',
              backgroundColor: 'rgba(120, 187, 241, 0.14)',
              transform: 'scale(1.1)',
            },
          }}
        >
          <MoreVert fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <ModernDataTable
      title={title}
      subtitle={subtitle}
      rows={consultationRequests}
      columns={columns}
      initialPageSize={10}
      pageSizeOptions={[5, 10, 25]}
      autoHeight
      hideFooter
    />
  );
};
